import type { PointerEvent } from "react";

/**
 * Whether the device's PRIMARY input can hover (a mouse or trackpad). False on a phone or tablet, where the
 * shared tooltip never opens from touch: a tooltip opened on a long press is positioned once, so it stays
 * behind when the draggable toolbar it labels moves. Read once: a device's primary input does not change
 * mid-session.
 */
export const PRIMARY_INPUT_CAN_HOVER =
  typeof globalThis.matchMedia === "function" && globalThis.matchMedia("(hover: hover)").matches;

/**
 * Hover is a mouse/pen concept. A tap swaps the toolbar icon for its highlighted version under the
 * finger, and the touch's "leave" then lands on the removed element, so a highlight set on touch sticks.
 */
export const isHoverPointer = (event: Pick<PointerEvent, "pointerType">): boolean => event.pointerType !== "touch";

/**
 * Controls inside a draggable panel that must not start a drag. react-draggable calls preventDefault on
 * every touchstart it handles, which cancels the tap's click, so on a phone a control this does not match
 * cannot be pressed. The panel still drags from its padding. `.e-input-group` (Syncfusion),
 * `.MuiFormControl-root` and `.MuiSlider-root` (MUI) are widgets whose own padding, label or rail is part
 * of the control rather than of the panel.
 */
export const DRAG_CANCEL_SELECTOR = [
  "button",
  "input",
  "select",
  "textarea",
  "a",
  "label",
  "[role='button']",
  "[role='checkbox']",
  "[role='combobox']",
  "[role='menuitem']",
  "[role='option']",
  "[role='radio']",
  "[role='slider']",
  "[role='switch']",
  "[role='tab']",
  ".e-input-group",
  ".MuiFormControl-root",
  ".MuiSlider-root",
].join(", ");
